/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, Alert, Skeleton, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { chartColours } from '../../theme/constants';
import QuestionAnswers from './QuestionAnswers';
import ChooseView from './ChooseView';
import Comments from './Comments';

const ChartWrapper = ({ type, name, answers, columns, title, comments, description, viewOptions, defaultView, inputType = null }) => {
    const [view, setView] = useState(defaultView);
    const [colours, setColours] = useState(null);

    const hasAnswers = Boolean(answers?.length > 0);

    const hasChildren = Boolean(type === 'matrix' || type === 'matrixdynamic' || type === 'multipletext' || type === 'matrixdropdown');

    const gridSpan = hasChildren ? 12 : 6;

    useEffect(() => {
        const allColours = chartColours.map(e => e.value);
        let colours = [];

        if (type === 'matrixdropdown') {
            const seenChoices = new Set();
            const usedColours = new Set();

            let colourIndex = 0;

            answers.forEach(column => {
                column.values.forEach(row => {
                    row.choices.forEach(({ name }) => {
                        if (!seenChoices.has(name)) {
                            seenChoices.add(name);

                            const colour = colourIndex % allColours.length;

                            while (usedColours.has(colour)) {
                                colourIndex++;
                            }

                            const assignedColour = allColours[colour];

                            usedColours.add(colour);
                            colourIndex++;

                            colours.push({
                                choice: name,
                                colour: assignedColour
                            });
                        }
                    });
                });
            });

            setColours(colours);
        }

        if (type === 'matrixdynamic' || type === 'multipletext') {
            let colourIndex = 0;

            answers.map(a => colours.push({
                key: a.key,
                colours: a.values?.map(() => {
                    colourIndex++;

                    return allColours[colourIndex % allColours.length];
                }) ?? []
            }));

            setColours(colours);
        }

        else if (answers?.length > 0) {
            for (let i = 0; i < answers.length; i++) {
                colours.push(allColours[i % allColours.length]);
            }

            setColours(colours);
        }
    }, [answers]);

    return (
        <Grid size={{ xs: 12, md: gridSpan }}>
            <Card variant="outlined">
                <CardHeader
                    title={title ?? name}
                    subheader={description ?? undefined}
                    action={(
                        <ChooseView
                            view={view}
                            onChangeView={setView}
                            views={viewOptions}
                        />
                    )}
                    sx={{
                        m: 0,
                        py: 1,
                        '& .MuiCardHeader-action': {
                            alignSelf: 'center',
                            pr: 1
                        }
                    }}
                />
                <CardContent
                    sx={{
                        pt: 0,
                        pb: 2,
                        '&:last-child': {
                            pb: 2
                        }
                    }}
                >
                    {hasAnswers ? colours ? (
                        <Stack spacing={2}>
                            <QuestionAnswers
                                data={answers}
                                view={view}
                                colours={colours}
                                questionType={type}
                                inputType={inputType}
                                columnInputTypes={columns?.map(e => ({ title: e.title, inputType: e.inputType }))}
                            />
                            {comments.length > 0 && (
                                <Comments
                                    comments={comments}
                                    columns={Boolean(type === 'matrixdropdown' || type === 'matrixdynamic')}
                                    rows={Boolean(type === 'matrixdropdown')}
                                />
                            )}
                        </Stack>
                    ) : (
                        <Skeleton width="100%" />
                    ) : (
                        <Alert severity="info">
                            No responses yet
                        </Alert>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
};

export default ChartWrapper;